/**
 * LiveTrades — displays recent executed trades with animated entry/exit
 */
import { useState, useEffect } from 'react';

export interface ExecutedTrade {
  id: string;
  symbol: string;
  side: 'BUY' | 'SELL';
  quantity: number;
  price: number;
  timestamp: number;
  notional: number;
}

interface Props {
  trades: ExecutedTrade[];
  onReset?: () => void;
}

export function LiveTrades({ trades, onReset }: Props) {
  const [animatingIds, setAnimatingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (trades.length === 0) return;
    const lastTrade = trades[trades.length - 1]!;
    setAnimatingIds((prev) => new Set([...prev, lastTrade.id]));
    const timer = setTimeout(() => {
      setAnimatingIds((prev) => {
        const next = new Set(prev);
        next.delete(lastTrade.id);
        return next;
      });
    }, 2000);
    return () => clearTimeout(timer);
  }, [trades]);

  if (trades.length === 0) {
    return (
      <div className="panel">
        <h3 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Live Trades</span>
          <button
            type="button"
            onClick={onReset}
            style={{
              fontSize: '0.85em',
              padding: '4px 8px',
              backgroundColor: 'var(--dark-bg)',
              color: 'var(--text-muted)',
              border: '1px solid var(--border)',
              cursor: 'pointer',
              borderRadius: 4,
            }}
          >
            Reset Demo
          </button>
        </h3>
        <div className="hint">No trades yet. Click Buy or Sell to execute.</div>
      </div>
    );
  }

  return (
    <div className="panel">
      <h3 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Live Trades ({trades.length})</span>
        <button
          type="button"
          onClick={onReset}
          style={{
            fontSize: '0.85em',
            padding: '4px 8px',
            backgroundColor: 'var(--dark-bg)',
            color: 'var(--text-muted)',
            border: '1px solid var(--border)',
            cursor: 'pointer',
            borderRadius: 4,
          }}
        >
          Reset Demo
        </button>
      </h3>
      <div style={{ maxHeight: 200, overflowY: 'auto' }}>
        {trades.map((trade) => {
          const isAnimating = animatingIds.has(trade.id);
          const time = new Date(trade.timestamp).toLocaleTimeString();
          return (
            <div
              key={trade.id}
              style={{
                padding: 8,
                marginBottom: 4,
                borderLeft: `4px solid ${trade.side === 'BUY' ? 'var(--green)' : 'var(--red)'}`,
                backgroundColor: trade.side === 'BUY' ? 'rgba(0,255,0,0.05)' : 'rgba(255,0,0,0.05)',
                borderRadius: 4,
                animation: isAnimating ? 'slideIn 0.3s ease-out' : 'none',
                fontSize: '0.85em',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontWeight: 600, color: trade.side === 'BUY' ? 'var(--green)' : 'var(--red)' }}>
                  {trade.side} {trade.quantity.toLocaleString()} {trade.symbol}
                </span>
                <span style={{ color: 'var(--text-muted)' }}>{time}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8em' }}>
                <span>@ {trade.price.toFixed(2)}</span>
                <span style={{ fontWeight: 600 }}>{(trade.notional).toLocaleString('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 })}</span>
              </div>
            </div>
          );
        })}
      </div>
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
}
